import React, { useMemo } from 'react';
import { Trans } from 'react-i18next';
import SkeletonLoader from 'Douleutaras/components/Loaders/SkeletonLoader';
import { QuestionsRef } from 'views/job/post-job/steps/QuestionsStep';
import { PostJobFormAttributes, TransformedFormData } from 'Douleutaras/redux/data/jobs/types';
import { transformFormDataToJobAttributes, transformJobAttributesToFormData } from 'Douleutaras/redux/data/jobs/helpers/data-transform';
import { loading } from 'Douleutaras/redux/data/ui/spinSlice';
import { postJob, postJobTmp, transformJobAttributesToUserData } from 'Douleutaras/redux/data/jobs/jobsSlice';
import { ApiErrorObject } from 'Douleutaras/types';
import apiV3ErrorProcessor from 'Douleutaras/utilities/normalizers/api-errror-normalizer';
import { addSnackbarNotification, SnackbarNotificationType } from 'Douleutaras/redux/data/ui/snackbarSlice';
import { createAndAuthenticate, selectCurrentUser } from 'Douleutaras/redux/data/auth/authSlice';
import { buildUrl, getAbsoluteUrl, VALID_URLS } from 'Douleutaras/utilities/url-builder';
import { useAppDispatch, useAppSelector } from 'Douleutaras/redux/hooks';
import { useNavigate } from 'react-router-dom';
import { sellerProfilesSelectors } from 'Douleutaras/redux/data/seller/sellerProfilesSlice';
import { POST_JOB_FLOW } from 'Douleutaras/views/job/post-job/types';
import { selectMe } from 'Douleutaras/redux/data/users/meSlice';
import useCookieConsents from 'Douleutaras/hooks/cookies/useCookieConsents';
import { getGclid } from 'Douleutaras/lib/marketing-tags-storage';

export enum StepStatus {
  HAS_NEXT,
  CAN_SKIP,
  IS_FINAL
}

export interface PostJobFormRef {
  questionsRef: { current: QuestionsRef };
  showNextButton: boolean;
  categoryId: string;
  currentStep: PostJobSteps;
  stepBack(): void;
  switchToNextStep(): void;
  switchStepBack(): void;
  goToNextStep(): void;
  confirmLocationMap(): void;
  closeLocationMap(): void;
}

export enum PostJobSteps {
  CATEGORY_STEP = 'category_step',
  QUESTIONS_STEP = 'questions_step',
  LOCATION_STEP = 'location_step',
  USER_STEP = 'user_step',
  DIAGNOSIS_STEP = 'diagnosis_step'
}

const usePostJobHelpers = ({
                             currentStep,
                             postJobFlow,
                             goToStep,
                             formMethods,
                             hasCallOutFeeStep,
                             proId,
                             setIsFormSubmitting,
                             setPrevStep,
                             userGis,
                             postJobSettings,
                             autoDealIds,
                             isProAvatarsVariant,
                             bidObjective
                           }) => {
  const profile = useAppSelector(state => sellerProfilesSelectors.selectById(state, proId));
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectMe);
  const storedUser = useAppSelector(selectCurrentUser);
  const isLoggedIn = Boolean(storedUser?.id);
  const { handleSubmit, setError, watch, reset } = formMethods;
  const navigate = useNavigate();
  // marketing data
  const { getLiveConsents } = useCookieConsents();
  const gclid = getGclid();

  const removeJobLocalStorageData = async () => {
    // Removed saved data from local storage
    await localStorage.removeItem('job-form');
    await localStorage.removeItem('pro-list-url-params');
  };

  // Post the actual job
  const onSubmit = async (data: Partial<PostJobFormAttributes>) => {
    const cookieConsents = getLiveConsents();
    const transformedFormData = transformJobAttributesToFormData(
      {
        flow: postJobFlow,
        formData: data,
        gclid,
        cookieConsents
      }
    );

    setIsFormSubmitting(true);
    dispatch(loading(true));

    let jobPostResponse = await dispatch(
      isLoggedIn || data.existingUser
        ? postJob({ formData: transformedFormData })
        : postJobTmp({ formData: transformedFormData })
    )
      .unwrap();

    if (jobPostResponse?.hasOwnProperty('errors')) {
      // Transform error object from API into our form-supported format
      const transformedErrors =
        transformFormDataToJobAttributes({ attributes: jobPostResponse?.errors } as TransformedFormData) as ApiErrorObject;

      // Log errors if needed
      apiV3ErrorProcessor(transformedErrors, setError);
      dispatch(
        addSnackbarNotification({
          duration: 5000, // in ms
          type: SnackbarNotificationType.ERROR,
          message: <Trans>T.POST-JOB.ERROR POSTING JOB</Trans>
        })
      );

      dispatch(loading(false));
      setIsFormSubmitting(false);

      return;
    }

    // If user doesn't exist & not logged-out existing user
    if (!isLoggedIn && !data.existingUser) {
      // First, register the user
      await dispatch(
        createAndAuthenticate(transformJobAttributesToUserData({ formData: data, userGis }))
      );
      // Then, post the same job as "tmp_job"
      jobPostResponse = await dispatch(
        postJob({ formData: transformedFormData })
      )
        .unwrap();
    }

    const inIframe = window.self !== window.top;

    // Handle duplicate requests
    if (jobPostResponse.isDuplicate) {
      // Removed saved data from local storage
      await removeJobLocalStorageData();
      const duplicateUrl = new URL(`${window.location.origin}${buildUrl(VALID_URLS.CLIENT_DASHBOARD_JOB,
        jobPostResponse.data.attributes.job_id)}`);
      if (jobPostResponse.data.attributes.access_token) {
        duplicateUrl.searchParams.set('token', jobPostResponse.data.attributes.access_token);
      }
      if (inIframe) {
        duplicateUrl.searchParams.set('duplicate', '1');
        window.top.location.href = duplicateUrl.href;
      } else {
        navigate(
          {
            pathname: duplicateUrl.pathname,
            search: duplicateUrl.search
          },
          {
            state: { duplicate: true },
            replace: true
          }
        );
      }

      return;
    }

    // Redirect to jobDone page
    if (jobPostResponse?.data?.id) {
      // Removed saved data from local storage
      await removeJobLocalStorageData();

      const afterPostJobUrl = new URL(`${window.location.origin}${buildUrl(VALID_URLS.JOB_DONE, jobPostResponse.data.id)}`);

      if (jobPostResponse?.data.attributes.accessToken) {
        afterPostJobUrl.searchParams.set('token', jobPostResponse?.data.attributes.accessToken);
      }

      // If we're in an iframe, redirect the parent
      if (inIframe) {
        if (postJobFlow) {
          afterPostJobUrl.searchParams.set('flow', postJobFlow);
        }
        window.top.location.href = afterPostJobUrl.href;

        return;
      }

      // Navigate to the success page in SPA style
      navigate({
        pathname: afterPostJobUrl.pathname,
        search: afterPostJobUrl.search
      }, {
        state: {
          proId,
          flow: postJobFlow,
          autoDealIdsLength: autoDealIds?.length || 0
        }
      });
    } else {
      dispatch(loading(false));
      setIsFormSubmitting(false);
    }
  };

  const getCurrentTitle = () => {
    // sets the title
    let title: string | React.ReactElement = '';
    let note: string | React.ReactElement = '';

    switch (true) {
      case currentStep === PostJobSteps.CATEGORY_STEP:
        title = <Trans>T.POST-JOB.SELECT CATEGORY</Trans>;
        note = undefined;
        break;
      case currentStep === PostJobSteps.QUESTIONS_STEP:
        title = <div><SkeletonLoader type="line" height={25}/></div>;
        note = '';
        break;

      case currentStep === PostJobSteps.LOCATION_STEP && Boolean(proId):
        title = <Trans>T.POST-JOB.LOCATION TITLE</Trans>;
        note = <Trans>T.POST-JOB.LOCATION NOTE</Trans>;
        break;

      case currentStep === PostJobSteps.LOCATION_STEP && !Boolean(proId):
        title = <Trans>T.POST-JOB.LOCATION TITLE</Trans>;
        note = <Trans>T.POST-JOB.MULTI LOCATION NOTE</Trans>;
        break;

      // Fast-lane multi bid
      case currentStep === PostJobSteps.USER_STEP && (
        postJobFlow === POST_JOB_FLOW.FASTLANE_MULTI && autoDealIds?.length > 1):

        title = <Trans values={{ name: profile?.attributes.sellerDisplayName }}>T.POST-JOB.FASTLANE MULTI USER TITLE</Trans>;
        note = '';
        break;

      case currentStep === PostJobSteps.USER_STEP && autoDealIds?.length > 1:
        title = <Trans>T.POST-JOB.MULTI PRO TITLE</Trans>;
        note = '';
        break;

      case currentStep === PostJobSteps.USER_STEP && isLoggedIn:
        title = <Trans>T.POST-JOB.USER TITLE LOGGED</Trans>;
        note = '';
        break;

      // Fast-lane single bid
      case currentStep === PostJobSteps.USER_STEP && (
        postJobFlow === POST_JOB_FLOW.FASTLANE_SINGLE && autoDealIds?.length === 1):

        title = <Trans values={{ name: profile?.attributes.sellerDisplayName }}>T.POST-JOB.FASTLANE SINGLE USER TITLE</Trans>;
        note = '';
        break;

      case currentStep === PostJobSteps.USER_STEP && (
          (Boolean(proId) && postJobFlow === POST_JOB_FLOW.SINGLE_BID) || autoDealIds?.length === 1):
        title = <Trans values={{ name: profile?.attributes.sellerDisplayName }}>T.POST-JOB.SINGLE-BID USER TITLE</Trans>;
        note = '';
        break;

      case currentStep === PostJobSteps.USER_STEP && Boolean(proId):
        title = <Trans values={{ name: profile?.attributes.sellerDisplayName }}>T.POST-JOB.USER TITLE</Trans>;
        note = <Trans values={{ name: profile?.attributes.sellerDisplayName }}>T.POST-JOB.USER NOTE</Trans>;
        break;

      case currentStep === PostJobSteps.USER_STEP && !Boolean(proId):
        title = <Trans>T.POST-JOB.MULTI USER TITLE</Trans>;
        note = <Trans>T.POST-JOB.USER NOTE LOGGED</Trans>;
        break;

      case currentStep === PostJobSteps.DIAGNOSIS_STEP:
        title = <Trans values={{ name: profile?.attributes.sellerDisplayName }}>T.POST-JOB.CALL OUT TITLE</Trans>;
        note = <Trans values={{ name: profile?.attributes.sellerDisplayName }}>T.POST-JOB.CALL OUT NOTE</Trans>;
        break;

      default:
        break;
    }

    return { title, note };
  };

  const gotoLocationStepDestination = async () => {
    const currentFormValues = watch();

    switch (true) {
      case postJobSettings.REDIRECT_TO_PRO_LIST:
        const proListParams = new URLSearchParams();
        proListParams.set('category', currentFormValues.categoryId);
        proListParams.set('loc', currentFormValues.location);
        proListParams.set('categoryFormId', currentFormValues.categoryFormId);
        proListParams.set('county', currentFormValues.county);
        proListParams.set('municipality', currentFormValues.municipality);
        proListParams.set('flow', postJobFlow);

        localStorage.setItem('pro-list-url-params', JSON.stringify({
          [currentFormValues.categoryId]: proListParams.toString()
        }));

        const absoluteUrl = getAbsoluteUrl('URLS.CLIENT_DASHBOARD.PRO_LIST.INDEX');
        if (window.top) {
          window.top.location.href = `${absoluteUrl}?${proListParams.toString()}`;

          return;
        }

        navigate({
          pathname: getAbsoluteUrl('URLS.CLIENT_DASHBOARD.PRO_LIST.INDEX'),
          search: proListParams.toString()
        }, {
          state: {
            flow: postJobFlow
          }
        });
        break;
      case (!isLoggedIn || postJobSettings.EXISTING_USER_CONTACT_DETAILS_STEP || (isLoggedIn && !user.phone)):
        goToStep(PostJobSteps.USER_STEP);
        break;
      case hasCallOutFeeStep:
        goToStep(PostJobSteps.DIAGNOSIS_STEP);
        break;
      default:
        handleSubmit(onSubmit)();
        break;
    }
  };

  const goToNextStep = (): void => {
    const lastCategoryId = watch().categoryId;

    switch (true) {
      default:
      case currentStep === PostJobSteps.CATEGORY_STEP:
        const currentValues = watch();
        if (currentValues.categoryId !== lastCategoryId) {
          reset({ ...currentValues, questions: {} });
        }
        goToStep(PostJobSteps.QUESTIONS_STEP);
        break;

      case currentStep === PostJobSteps.QUESTIONS_STEP:
        goToStep(PostJobSteps.LOCATION_STEP);
        break;

      case currentStep === PostJobSteps.LOCATION_STEP:
        gotoLocationStepDestination();
        break;

      case currentStep === PostJobSteps.USER_STEP:
        // check if there is diagnosis step
        if (hasCallOutFeeStep) {
          goToStep(PostJobSteps.DIAGNOSIS_STEP);
        } else {
          handleSubmit(onSubmit)();

          return;
        }
        break;

      case currentStep === PostJobSteps.DIAGNOSIS_STEP:
        handleSubmit(onSubmit)();

        return;
    }

    setPrevStep(prevSteps => [...prevSteps, currentStep ?? PostJobSteps.CATEGORY_STEP ]);
  };

  // Next button text. Can be custom
  const determineNextButtonText = stepStatus => {
    switch (true) {
      // Personal-details A/B (variant B): the submitted CTA becomes a quote-oriented label.
      // Submit behavior is unchanged — only the label differs from control.
      case currentStep === PostJobSteps.USER_STEP && isProAvatarsVariant && bidObjective > 1:
        return <Trans
          i18nKey="T.POST-JOB.PROS-BANNER.CTA-PLURAL"
          defaults="Get {{ count }} quotes"
          values={{ count: bidObjective }} />;

      case currentStep === PostJobSteps.USER_STEP && isProAvatarsVariant && bidObjective >= 1:
        return <Trans i18nKey="T.POST-JOB.PROS-BANNER.CTA-SINGULAR" defaults="Get a quote" />;

      case currentStep === PostJobSteps.LOCATION_STEP && postJobSettings.REDIRECT_TO_PRO_LIST:
        return <Trans>T.POST-JOB.SEARCH PROS</Trans>;

      default:
      case currentStep === PostJobSteps.LOCATION_STEP && !Boolean(user?.phone):
      case stepStatus === StepStatus.HAS_NEXT:
        return <Trans>T.POST-JOB.NEXT</Trans>;

      case stepStatus === StepStatus.IS_FINAL && autoDealIds?.length > 1:
        return <Trans
          i18nKey="T.POST-JOB.INFORM {{ count }} PROFESSIONALS"
          values={{ count: autoDealIds.length }} />;

      case stepStatus === StepStatus.IS_FINAL && (Boolean(proId) || autoDealIds?.length === 1):
        return <Trans>T.POST-JOB.FIND PROFESSIONAL</Trans>;

      case stepStatus === StepStatus.IS_FINAL && !Boolean(proId):
        return <Trans>T.POST-JOB.FIND MULTI PROFESSIONALS</Trans>;

      case stepStatus === StepStatus.CAN_SKIP:
        return <Trans>T.POST-JOB.SKIP</Trans>;
    }
  };

  return {
    determineNextButtonText,
    getCurrentTitle,
    goToNextStep,
    onSubmit
  };
};

export default usePostJobHelpers;

export const usePostJobProgress = (currentStep?: PostJobSteps, questionsInnerProgress: number = 0) => {
  const progressPercent = useMemo(() => {
    switch (currentStep) {
      case PostJobSteps.CATEGORY_STEP:
        return 0;
      case PostJobSteps.QUESTIONS_STEP:
        // tslint:disable-next-line
        return 36 + Math.round(questionsInnerProgress * 34);
      case PostJobSteps.LOCATION_STEP:
        return 85;
      case PostJobSteps.USER_STEP:
        return 90;
      case PostJobSteps.DIAGNOSIS_STEP:
        return 95;
      default:
        return 0;
    }
  }, [currentStep, questionsInnerProgress]);

  const progressTranslateX = `${progressPercent - 100}%`;

  return { progressPercent, progressTranslateX };
};
